import { ChangeEvent } from 'react';
import { DeepMap, FieldError, UseFormMethods } from 'react-hook-form';
import {
  buildTrunkPayloadForCloneRequest,
  calculateNicLimits,
  getSubnet,
  getTrunkedNetworkUidsFromNic,
} from '../Utils';
import i18n from '../../../../../../../i18n';

const orderNics = (mappings: NetworkToConfigure[]): NetworkToConfigure[] => {
  return mappings.sort(
    (a, b) =>
      parseInt((a.name || '').replace(/[^0-9]/g, '')) -
      parseInt((b.name || '').replace(/[^0-9]/g, '')),
  );
};

export const applyTrunkFieldsFromSourceNic = (
  sourceNic: { trunk?: TrunkedNetworkInterface },
  row: NetworkToConfigure,
): NetworkToConfigure => {
  const trunked = Boolean(sourceNic.trunk?.enabled);
  if (!trunked) {
    return {
      ...row,
      trunked: false,
      trunkedNetworks: [],
    };
  }

  return {
    ...row,
    assignDhcp: false,
    ipAddress: '',
    trunked: true,
    trunkedNetworks: getTrunkedNetworkUidsFromNic(sourceNic),
  };
};

export const prepInterfacesForTemplatesConfigureScreen = (
  interfaces: NetworkToConfigure[],
  networks: Network[],
): NetworkToConfigure[] => {
  const mappings: NetworkToConfigure[] = interfaces.map((networkInterface) => {
    let networkUid: string | undefined;
    networks.forEach((network) =>
      network.inventoryNetwork.id === networkInterface.inventoryNetworkId
        ? (networkUid = network.uid)
        : null,
    );
    return applyTrunkFieldsFromSourceNic(
      networkInterface as { trunk?: TrunkedNetworkInterface },
      networkUid
        ? {
            ...networkInterface,
            network: networkUid,
          }
        : {
            ...networkInterface,
          },
    );
  });

  return orderNics(calculateNicLimits(mappings, networks));
};

export const prepInterfacesForDemosConfigureScreen = (
  interfaces: NetworkToConfigure[],
  networks: Network[],
): NetworkToConfigure[] => {
  const mappings: NetworkToConfigure[] = interfaces.map((networkInterface) => {
    let networkUid: string | undefined;
    networks.some((network) =>
      network.inventoryNetwork.id ===
      networkInterface.network?.inventoryNetwork?.id
        ? (networkUid = network.uid)
        : null,
    );
    const row: NetworkToConfigure = {
      assignDhcp: networkInterface.assignDhcp,
      ipAddress: networkInterface.ipAddress,
      macAddress: networkInterface.macAddress,
      name: networkInterface.name,
      ...(networkUid ? { network: networkUid } : {}),
    };

    return applyTrunkFieldsFromSourceNic(
      networkInterface as { trunk?: TrunkedNetworkInterface },
      row,
    );
  });

  return orderNics(calculateNicLimits(mappings, networks));
};

export const handleTrunkedChange =
  (
    interfaces: NetworkToConfigure[],
    setValue: UseFormMethods<AddVmConfigureFormValues>['setValue'],
    trigger: UseFormMethods<AddVmConfigureFormValues>['trigger'],
  ) =>
  (index: number) =>
  (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setValue(`interfaces.${index}.trunked`, checked, {
      shouldValidate: false,
    });
    if (checked) {
      setValue(`interfaces.${index}.assignDhcp`, false, {
        shouldValidate: false,
      });
      setValue(`interfaces.${index}.ipAddress`, '', {
        shouldValidate: false,
      });
      setValue(
        `interfaces.${index}.trunkedNetworks`,
        interfaces[index].trunkedNetworks?.length
          ? interfaces[index].trunkedNetworks
          : [],
        { shouldValidate: false },
      );
    } else {
      setValue(`interfaces.${index}.trunkedNetworks`, [], {
        shouldValidate: false,
      });
    }

    trigger(`interfaces.${index}.network`);

    if (!checked) {
      trigger(`interfaces.${index}.trunkedNetworks`);
    }

    trigger(`interfaces.${index}.ipAddress`);
    trigger(`interfaces.${index}.macAddress`);
  };

export const determineIpErrorToDisplay = (
  errors: DeepMap<AddVmConfigureFormValues, FieldError>,
  index: number,
  networks: Network[],
  selectedNetworkUid: string | undefined,
): string | undefined => {
  if (!errors.interfaces?.[index]?.ipAddress?.message || !selectedNetworkUid) {
    return undefined;
  }
  if (
    errors.interfaces?.[index]?.ipAddress?.message ===
    'The IP must be in the correct range for this network'
  ) {
    const subnet = getSubnet(selectedNetworkUid, networks);
    return `${errors.interfaces?.[index]?.ipAddress?.message} ${subnet}`;
  }
  if (
    errors.interfaces?.[index]?.ipAddress?.message !==
    i18n.t('virtualMachines.add.duplicateIp')
  ) {
    return errors.interfaces?.[index]?.ipAddress?.message;
  } else {
    return undefined;
  }
};

export const determineMacErrorToDisplay = (
  errors: DeepMap<AddVmConfigureFormValues, FieldError>,
  index: number,
  networks: Network[],
  selectedNetworkUid: string | undefined,
): string | undefined => {
  if (!errors.interfaces?.[index]?.macAddress?.message || !selectedNetworkUid) {
    return undefined;
  }
  if (
    errors.interfaces?.[index]?.macAddress?.message !==
    i18n.t('virtualMachines.add.duplicateMac')
  ) {
    return errors.interfaces?.[index]?.macAddress?.message;
  } else {
    return undefined;
  }
};

export const determineNetworkErrorToDisplay = (
  errors: DeepMap<AddVmConfigureFormValues, FieldError>,
  index: number,
  selectedNetworkUid: string | undefined,
  networks: Network[],
): string | undefined => {
  if (
    errors.interfaces?.[index]?.network?.message === 'Please select a network'
  ) {
    return errors.interfaces?.[index]?.network?.message;
  }

  if (!selectedNetworkUid) {
    return undefined;
  }

  if (
    errors.interfaces?.[index]?.network?.message ===
    'Network connection limit of'
  ) {
    const network = networks.filter(
      (network) => network.uid === selectedNetworkUid,
    )[0];
    const limit = network.vmConnectionInfo.maxConnections;

    return `${i18n.t(
      'virtualMachines.configure.select.limitErrorPt1',
    )} ${limit} ${i18n.t('virtualMachines.configure.select.limitErrorPt2')}`;
  } else {
    return errors.interfaces?.[index]?.network?.message;
  }
};

export const prepMappingsForCreate = (
  interfaces: AddVmConfigureFormValues['interfaces'],
): CreateVmNetworkInterface[] => {
  const interfacesLessNulls = interfaces.filter((item) => item !== null);
  return interfacesLessNulls.map((interfaceItem) => {
    if (interfaceItem.trunked) {
      return {
        macAddress: interfaceItem.macAddress || undefined,
        name: interfaceItem.name!,
        trunk: buildTrunkPayloadForCloneRequest(
          interfaceItem.trunkedNetworks ?? [],
        ),
        type: interfaceItem.type || undefined,
      };
    }

    return {
      assignDhcp: interfaceItem.assignDhcp || false,
      ipAddress: interfaceItem.ipAddress || undefined,
      macAddress: interfaceItem.macAddress || undefined,
      name: interfaceItem.name!,
      network: {
        uid: interfaceItem.network!,
      },
      type: interfaceItem.type || undefined,
    };
  });
};

export const determineShowDeleteIcon = (
  interfaces: AddVmConfigureFormValues['interfaces'],
): boolean => {
  if (!interfaces) {
    return false;
  }
  const interfacesLessNulls = interfaces.filter((item) => item);
  return interfacesLessNulls.length > 1;
};

type GetDisplayedNetworkRowIndices = {
  hiddenRows: number[];
  rowCount: number;
};

export const getDisplayedNetworkRowIndices = ({
  hiddenRows,
  rowCount,
}: GetDisplayedNetworkRowIndices): number[] => {
  const hiddenRowSet = new Set(hiddenRows);
  const sequentialRowIndices = Array.from(
    { length: rowCount },
    (_, index) => index,
  );

  return sequentialRowIndices.filter((index) => !hiddenRowSet.has(index));
};

type ShouldExpandFormContainerForDropdown = {
  hiddenRows: number[];
  openDropdownRowIndex?: number;
  rowCount: number;
};

export const shouldExpandFormContainerForDropdown = ({
  hiddenRows,
  openDropdownRowIndex,
  rowCount,
}: ShouldExpandFormContainerForDropdown): boolean => {
  if (openDropdownRowIndex === undefined) {
    return false;
  }

  const displayedRowIndices = getDisplayedNetworkRowIndices({
    hiddenRows,
    rowCount,
  });

  if (displayedRowIndices.length === 0) {
    return false;
  }

  return (
    openDropdownRowIndex === displayedRowIndices[displayedRowIndices.length - 1]
  );
};
